import type { CustomFormErrors, FormErrors, FormErrorsWithArray } from 'redux-form';

import type { W_Job_CreateInvoice_RM } from 'ab-api/web/job/createInvoice';
import type { W_Job_FindInvoiceById_VM } from 'ab-api/web/job/findInvoiceById';
import type { W_Job_UpdateInvoice_RM } from 'ab-api/web/job/updateInvoice';

import { getId } from '@acceligentllc/shared/utils/array';
import { filterContactMethod } from '@acceligentllc/shared/utils/contact';
import { isValidEmail } from '@acceligentllc/shared/utils/email';
import { isValidTextInput } from '@acceligentllc/shared/utils/text';
import * as TimeUtils from '@acceligentllc/shared/utils/time';

import type { PhoneTypes } from '@acceligentllc/shared/enums/contactMethodType';
import { EmailTypes, EmailTypesArray } from '@acceligentllc/shared/enums/contactMethodType';
import type { FileType } from '@acceligentllc/shared/enums/fileType';
import { InvoiceBillingLookupType } from '@acceligentllc/shared/enums/invoiceBillingLookupType';
import { InvoiceStatus } from '@acceligentllc/shared/enums/invoiceStatus';
import type { ResourceStatus } from '@acceligentllc/shared/enums/resourceStatus';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import InvoiceStatusDisplay from 'ab-enums/invoiceStatusDisplay.enum';

interface UserInfoFM {
	accountId?: Nullable<number>;
	userId?: number;
	firstName?: string;
	lastName?: string;
	fullName: string;
}
interface AttachmentFM {
	id: number;
	size: number;
	fileName: string;
	type: FileType;
	status: ResourceStatus;
	storageName: string;
	storageContainer: string;
	/** original version with presigned url */
	originalSrc: string;
	/** ISO_DATETIME */
	lastModifiedAt: string;
	uploadedBy: Nullable<UserInfoFM>;
}

class ContactMethodFM {
	id: number;
	contactId: number;
	type: PhoneTypes | EmailTypes;
	value: string;

	constructor(contactMethod) {
		this.id = contactMethod.id;
		this.contactId = contactMethod.contactId;
		this.type = contactMethod.type;
		this.value = contactMethod.value;
	}

	static toList(contactMethods, type: typeof EmailTypes | typeof PhoneTypes): ContactMethodFM[] {
		return contactMethods
			.filter((_method) => _method && Object.keys(type).includes(_method.type))
			.map((_method) => new ContactMethodFM(_method));
	}
}

export class ContactFM {
	id: number;
	contactId: number;
	title: Nullable<string>;
	fullName: string;
	companyName: Nullable<string>;
	emails: ContactMethodFM[];
	contactEmailIds: number[];

	constructor(_contactLookup) {
		if (!_contactLookup) {
			return;
		}

		const _contact = _contactLookup?.contact;

		this.id = _contactLookup.id;
		this.contactId = _contactLookup.contactId;
		this.title = _contact?.title;
		this.fullName = _contact?.fullName;
		this.companyName = _contact?.companyName;

		this.emails = ContactMethodFM.toList(_contact?.contactMethods ?? [], EmailTypes);

		this.contactEmailIds = filterContactMethod(_contactLookup.contactLookupMethods, EmailTypesArray);
	}

	static bulkConstructor = (_contactLookups) => _contactLookups.map((__contactLookup) => new ContactFM(__contactLookup));
}

type BillingEmail = {
	type: InvoiceBillingLookupType.EMAIL_ONLY;
	email: string;
};

type BillingContact = {
	contact: ContactFM;
	type: InvoiceBillingLookupType.CONTACT;
};

type InvoiceBillingContactFM = { id?: number; } & (BillingEmail | BillingContact);

interface BaseContactTableInfo {
	id: number;
	email: string;
	/** ISO_DATETIME */
	lastReminderSent: Nullable<string>;
}

type ContactMethod = BaseContactTableInfo & {
	type: InvoiceBillingLookupType.CONTACT;
	contactId: number;
	title: Nullable<string>;
	fullName: string;
	companyName: Nullable<string>;
	contactMethodId: number;
};

type EmailOnlyInfo = BaseContactTableInfo & BillingEmail;

type ContactTableInfo = EmailOnlyInfo | ContactMethod;

export class InvoiceFM {
	id?: number;
	invoiceCode: Nullable<string>;
	/** MM-DD-YYYY */
	dateCreated: Nullable<string>;
	/** MM-DD-YYYY */
	dueDate: Nullable<string>;
	/** MM-DD-YYYY */
	invoicingDate: Nullable<string>;
	status: InvoiceStatusDisplay;
	note: Nullable<string>;
	totalAmount: Nullable<number>;
	paidAmount: Nullable<number>;
	retainedAmount: Nullable<number>;
	outstandingDebt: Nullable<number>;
	sendReminderOnInvoice: boolean;
	excludeInternalUsersFromAutomaticReminders: boolean;
	excludeBillingContactsFromAutomaticReminders: boolean;
	billingContacts: Nullable<InvoiceBillingContactFM>[];
	attachments: AttachmentFM[];
	uploadedAttachmentIds?: number[];
	uploadingAttachments?: Record<string, true>;
	billingContactMethods: ContactTableInfo[];

	static _resolveInvoiceStatusForRM(invoiceStatus: InvoiceStatusDisplay): InvoiceStatus {
		return invoiceStatus === InvoiceStatusDisplay.DRAFT ? InvoiceStatus.DRAFT : InvoiceStatus.INVOICED;
	}

	static getAttributeName = (attribute: keyof InvoiceFM) => attribute;

	static fromVMtoFM(vm: W_Job_FindInvoiceById_VM): InvoiceFM {
		return {
			id: vm.id,
			invoiceCode: vm.invoiceCode,
			status: vm.status,
			dateCreated: TimeUtils.formatDate(vm.dateCreated, TimeFormat.DATE_ONLY, TimeFormat.DB_DATE_ONLY),
			dueDate: TimeUtils.formatDate(vm.dueDate, TimeFormat.DATE_ONLY, TimeFormat.DB_DATE_ONLY),
			invoicingDate: vm.invoicingDate
				? TimeUtils.formatDate(vm.invoicingDate, TimeFormat.DATE_ONLY, TimeFormat.DB_DATE_ONLY)
				: null,
			totalAmount: vm.totalAmount,
			paidAmount: vm.paidAmount,
			outstandingDebt: vm.outstandingDebt,
			retainedAmount: vm.retainedAmount,
			note: vm.note,
			excludeInternalUsersFromAutomaticReminders: vm.excludeInternalUsersFromAutomaticReminders,
			excludeBillingContactsFromAutomaticReminders: vm.excludeBillingContactsFromAutomaticReminders,
			sendReminderOnInvoice: vm.sendReminderOnInvoice,
			billingContacts: vm.billingContacts ?? [],
			attachments: vm.attachments.map((_att) => ({ ..._att, fileName: _att.name })),
			uploadedAttachmentIds: [],
			billingContactMethods: vm.billingContactMethods ?? [],
		};
	}

	static toCreateInvoiceRM(fm: InvoiceFM): W_Job_CreateInvoice_RM {
		if (!fm.invoiceCode || !fm.dateCreated || !fm.dueDate || !fm.status || fm.totalAmount === undefined || fm.totalAmount === null) {
			throw new Error('Form not filled out correctly');
		}

		return {
			id: fm.id,
			invoiceCode: fm.invoiceCode,
			status: InvoiceFM._resolveInvoiceStatusForRM(fm.status),
			dateCreated: TimeUtils.formatDate(fm.dateCreated, TimeFormat.DB_DATE_ONLY, TimeFormat.DATE_ONLY),
			dueDate: TimeUtils.formatDate(fm.dueDate, TimeFormat.DB_DATE_ONLY, TimeFormat.DATE_ONLY),
			invoicingDate: fm.invoicingDate,
			totalAmount: fm.totalAmount.toString(),
			retainedAmount: fm.retainedAmount?.toString() ?? '0',
			note: fm.note,
			sendReminderOnInvoice: fm.sendReminderOnInvoice,
			excludeInternalUsersFromAutomaticReminders: fm.excludeInternalUsersFromAutomaticReminders,
			excludeBillingContactsFromAutomaticReminders: fm.excludeBillingContactsFromAutomaticReminders,
			uploadedAttachmentIds: fm.uploadedAttachmentIds ?? [],
			billingContacts: fm.billingContacts?.reduce((_acc, _invoiceFM) => {
				if (!_invoiceFM) {
					return _acc;
				}

				switch (_invoiceFM.type) {
					case InvoiceBillingLookupType.EMAIL_ONLY: {
						_acc.push({
							id: _invoiceFM.id,
							type: InvoiceBillingLookupType.EMAIL_ONLY,
							email: _invoiceFM.email,
						});
						break;
					}
					case InvoiceBillingLookupType.CONTACT: {
						_acc.push({
							id: _invoiceFM.id,
							type: InvoiceBillingLookupType.CONTACT,
							contact: {
								id: _invoiceFM.contact.id,
								contactId: _invoiceFM.contact.contactId,
								contactEmailIds: _invoiceFM.contact.contactEmailIds,
							},
						});
						break;
					}
				}

				return _acc;
			}, [] as W_Job_CreateInvoice_RM['billingContacts']) ?? [],
			billingContactMethods: fm.billingContactMethods ?? [],
		};
	}

	static toUpdateInvoiceRM(fm: InvoiceFM): W_Job_UpdateInvoice_RM {
		if (!fm.invoiceCode || !fm.dateCreated || !fm.dueDate || !fm.status || fm.totalAmount === undefined || fm.totalAmount === null) {
			throw new Error('Form not filled out correctly');
		}

		return {
			id: fm.id,
			invoiceCode: fm.invoiceCode,
			status: InvoiceFM._resolveInvoiceStatusForRM(fm.status),
			invoicingDate: fm.invoicingDate,
			dateCreated: TimeUtils.formatDate(fm.dateCreated, TimeFormat.DB_DATE_ONLY, TimeFormat.DATE_ONLY),
			dueDate: TimeUtils.formatDate(fm.dueDate, TimeFormat.DB_DATE_ONLY, TimeFormat.DATE_ONLY),
			totalAmount: fm.totalAmount.toString(),
			retainedAmount: fm.retainedAmount?.toString() ?? '0',
			note: fm.note,
			sendReminderOnInvoice: fm.sendReminderOnInvoice,
			excludeInternalUsersFromAutomaticReminders: fm.excludeInternalUsersFromAutomaticReminders,
			excludeBillingContactsFromAutomaticReminders: fm.excludeBillingContactsFromAutomaticReminders,
			uploadedAttachmentIds: fm.uploadedAttachmentIds ?? [],
			attachmentIds: fm.attachments.map(getId),
			billingContacts: fm.billingContacts?.reduce((_acc, _invoiceFM) => {
				if (!_invoiceFM) {
					return _acc;
				}

				switch (_invoiceFM.type) {
					case InvoiceBillingLookupType.EMAIL_ONLY: {
						_acc.push({
							id: _invoiceFM.id,
							type: InvoiceBillingLookupType.EMAIL_ONLY,
							email: _invoiceFM.email,
						});
						break;
					}
					case InvoiceBillingLookupType.CONTACT: {
						_acc.push({
							id: _invoiceFM.id,
							type: InvoiceBillingLookupType.CONTACT,
							contact: {
								id: _invoiceFM.contact.id,
								contactId: _invoiceFM.contact.contactId,
								contactEmailIds: _invoiceFM.contact.contactEmailIds,
							},
						});
						break;
					}
				}

				return _acc;
			}, [] as W_Job_UpdateInvoice_RM['billingContacts']) ?? [],
			billingContactMethods: fm.billingContactMethods ?? [],
		};
	}

	static _checkBillingContactErrors = (_error) => !Object.keys(_error ?? {}).length;

	static validate = (form: InvoiceFM): FormErrors<InvoiceFM> => {

		const errors: CustomFormErrors<InvoiceFM> = {};

		if (!isValidTextInput(form?.invoiceCode)) {
			errors.invoiceCode = 'Invoice ID is required';
		}

		if (!form.dateCreated) {
			errors.dateCreated = 'Date created is required';
		} else if (form.dateCreated && !TimeUtils.isDateInCorrectFormat(form.dateCreated, TimeFormat.DATE_ONLY)) {
			errors.dateCreated = 'Date created is not in MM-DD-YYYY format';
		}
		if (!form.dueDate) {
			errors.dueDate = 'Due date is required';
		} else if (form.dueDate && !TimeUtils.isDateInCorrectFormat(form.dueDate, TimeFormat.DATE_ONLY)) {
			errors.dueDate = 'Due date is not in MM-DD-YYYY format';
		}

		if (form.totalAmount === undefined || form.totalAmount === null || form.totalAmount === 0) {
			errors.totalAmount = 'Total amount must be entered';
		}

		if (!!form.uploadingAttachments && Object.keys(form.uploadingAttachments).length) {
			errors.uploadingAttachments = 'Attachment upload in progress.';
		}

		return errors as FormErrors<InvoiceFM>;
	};

	static _hasInvalidBillingContact = (_billingContactError) => {
		return !!_billingContactError ? !!Object.keys(_billingContactError).length : false;
	};

	static saveFormEnabled = (form: InvoiceFM, errors: FormErrorsWithArray<InvoiceFM, string>): boolean => {
		if (!form) {
			return false;
		}
		if (!form.invoiceCode ||
			!form.dateCreated ||
			!form.dueDate ||
			form.totalAmount === undefined ||
			form.totalAmount === null) {
			return false;
		}

		if (form.billingContactMethods) {
			for (const _billingContactMethod of form.billingContactMethods) {
				if (
					// eslint-disable-next-line @typescript-eslint/prefer-optional-chain
					!_billingContactMethod ||
					!_billingContactMethod.email ||
					!_billingContactMethod.email.includes('@') ||
					!isValidEmail(_billingContactMethod.email)
				) {
					return false;
				}
			}
		}

		const areBillingContactsInvalid = errors.billingContacts?.some(InvoiceFM._hasInvalidBillingContact);

		if (areBillingContactsInvalid) {
			return false;
		}

		const thereAreAttachmentsInUpload = !!form.uploadingAttachments && Object.keys(form.uploadingAttachments).length;

		return !thereAreAttachmentsInUpload;
	};

}
